import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Collapse, Grid, InputAdornment, MenuItem, Paper, Stack,
  TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { ArrowBack, FilterList, ClearAll, PlayArrow, Search, Visibility } from '@mui/icons-material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker, type DateValidationError } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import PartnerMultiSelect from '../../components/partners/PartnerMultiSelect';
import {
  getReportDefinitions, runReport, getTripRequestReport, getPartnerWiseReport,
  type ReportColumn, type ReportDefinition, type ReportPersonInfo,
} from '../../services/reportService';
import { listZones } from '../../services/zoneService';
import type { PartnerRow } from '../../services/partnerUserService';
import { opsEntityPath } from '../../utils/opsEntityNav';
import { formatMoney } from '../../utils/currency';

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';
const DISPLAY_DATETIME_FORMAT = 'MM/DD/YYYY hh:mm:ss A';
const PAYMENT_OPTIONS = ['cash', 'card', 'upi', 'wallet', 'corporate'];
const TRIP_TYPES = ['LOCAL', 'RENTAL', 'OUTSTATION'];
const fieldSx = { '& .MuiInputBase-root': { bgcolor: 'background.paper' } };

type Filters = Record<string, string>;
interface AppliedState { filters: Filters; partners: PartnerRow[] }

function titleCase(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value: unknown) {
  if (!value) return '—';
  const d = dayjs(value as string);
  return d.isValid() ? d.format(DISPLAY_DATETIME_FORMAT) : String(value);
}

function pickerError(field: 'from' | 'to', error: DateValidationError | null) {
  if (!error) return undefined;
  if (error === 'invalidDate') return `Enter a valid date (${DISPLAY_DATE_FORMAT})`;
  if (error === 'disableFuture') return 'Date cannot be in the future';
  if (field === 'from' && error === 'maxDate') return 'From date cannot be after To date';
  if (field === 'to' && error === 'minDate') return 'To date cannot be before From date';
  return 'Invalid date';
}

function columnRole(c: ReportColumn): ReportColumn['role'] {
  if (c.role) return c.role;
  if (c.key === 'customer') return 'customer';
  if (['driver', 'partner', 'provider'].includes(c.key)) return 'partner';
  return undefined;
}

function PersonCell({ value, info, id, to, onOpen }: {
  value: unknown;
  info?: ReportPersonInfo | null;
  id: string;
  to: string;
  onOpen: (path: string) => void;
}) {
  const label = String(value ?? '') || '—';
  if (!id || label === '—' || label === '-' || label === 'Unassigned') {
    return <Typography variant="body2" color="text.secondary">{label}</Typography>;
  }
  const tip = (
    <Box>
      <Typography variant="caption" display="block" fontWeight={700}>{info?.name || label}</Typography>
      {info?.phone && <Typography variant="caption" display="block">Phone: {info.phone}</Typography>}
      {info?.email && <Typography variant="caption" display="block">Email: {info.email}</Typography>}
      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>ID: {id}</Typography>
    </Box>
  );
  return (
    <Tooltip title={tip} arrow>
      <Button
        size="small"
        sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0, justifyContent: 'flex-start', maxWidth: '100%' }}
        onClick={(e) => { e.stopPropagation(); onOpen(to); }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
      </Button>
    </Tooltip>
  );
}

export default function ReportRunPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();
  const [draft, setDraft] = useState<Filters>({});
  const [draftPartners, setDraftPartners] = useState<PartnerRow[]>([]);
  const [applied, setApplied] = useState<AppliedState>({ filters: {}, partners: [] });
  const [runCount, setRunCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [fromPickerError, setFromPickerError] = useState<DateValidationError | null>(null);
  const [toPickerError, setToPickerError] = useState<DateValidationError | null>(null);

  const { data: definitions } = useQuery({ queryKey: ['report-definitions'], queryFn: getReportDefinitions });
  const selected = (definitions || []).find((r) => r.slug === slug) as ReportDefinition | undefined;

  const isTripRequest = slug === 'trip-request-report';
  const isPartnerWise = slug === 'partner-wise-report';
  const isOperational = isTripRequest || isPartnerWise;

  const { data: zonesData } = useQuery({
    queryKey: ['zones-list-mini'],
    queryFn: () => listZones({ limit: 100 }),
    enabled: isOperational,
  });
  const zones = zonesData?.rows || [];
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';

  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    Object.entries(applied.filters).forEach(([k, v]) => { if (v && v.trim()) params[k] = v.trim(); });
    const driverIds = applied.partners.map((p) => p._id).join(',');
    if (driverIds) params.driverIds = driverIds;
    params.timezone = timezone;
    return params;
  }, [applied, timezone]);

  const { data: result, isFetching, isError, error } = useQuery({
    queryKey: ['report-run', slug, queryParams, runCount],
    queryFn: () => {
      if (isTripRequest) return getTripRequestReport(queryParams);
      if (isPartnerWise) return getPartnerWiseReport(queryParams);
      return runReport(slug, queryParams);
    },
    enabled: !!slug && (isOperational || !!selected),
    placeholderData: keepPreviousData,
  });

  const lastMeta = useRef(result?.meta);
  if (result?.meta) lastMeta.current = result.meta;
  const meta = result?.meta ?? lastMeta.current;
  const identity = meta?.identity;
  const filterOptions = meta?.filterOptions || {};
  const statusOptions = filterOptions.status || [];

  const rangeOrderError = draft.dateFrom && draft.dateTo && draft.dateTo < draft.dateFrom
    ? 'From date cannot be after To date'
    : undefined;
  const fromError = pickerError('from', fromPickerError) ?? rangeOrderError;
  const toError = pickerError('to', toPickerError) ?? (rangeOrderError ? 'To date cannot be before From date' : undefined);
  const hasFilterErrors = !!fromError || !!toError;

  const openPath = (path: string) => navigate(path, { state: { from: `/reports/${slug}` } });
  const openRow = (row: Record<string, unknown>) => {
    const path = opsEntityPath(row, { slug, dataSource: meta?.dataSource });
    if (path) openPath(path);
  };

  const columns: GridColDef[] = useMemo(() => {
    const cols = result?.columns || selected?.columns || [];
    const mapped: GridColDef[] = cols.map((c) => {
      const role = columnRole(c);
      const col: GridColDef = {
        field: c.key,
        headerName: c.label,
        flex: c.type === 'string' || !c.type ? 1 : undefined,
        minWidth: role ? 200 : c.type === 'string' ? 140 : 110,
        width: c.width || (c.type === 'currency' || c.type === 'number' ? 130 : c.type === 'date' ? 200 : undefined),
      };
      if (c.type === 'currency') {
        col.valueFormatter = (v) => (v != null ? formatMoney(v) : '—');
        col.align = 'right';
        col.headerAlign = 'right';
      } else if (c.type === 'date') {
        col.valueFormatter = (v) => formatDateTime(v);
        col.renderCell = (p) => (
          <Tooltip title={formatDateTime(p.value)}>
            <Typography variant="body2" noWrap>{formatDateTime(p.value)}</Typography>
          </Tooltip>
        );
      } else if (c.key === 'status') {
        col.valueFormatter = (v) => (v ? titleCase(String(v)) : '—');
        col.renderCell = (p) => (p.value ? <Chip size="small" variant="outlined" label={titleCase(String(p.value))} /> : '—');
      } else if (role === 'customer') {
        col.renderCell = (p) => {
          const row = p.row as Record<string, unknown>;
          const id = String(row.customerId || '');
          return (
            <PersonCell value={p.value} info={row.customerInfo as ReportPersonInfo | null} id={id}
              to={`/customers/${id}`} onOpen={openPath} />
          );
        };
      } else if (role === 'partner') {
        col.renderCell = (p) => {
          const row = p.row as Record<string, unknown>;
          const id = String(row.driverId || (isPartnerWise ? row._id : '') || '');
          return (
            <PersonCell value={p.value} info={row.partnerInfo as ReportPersonInfo | null} id={id}
              to={`/partners/${id}`} onOpen={openPath} />
          );
        };
      } else if (c.type === 'string' || !c.type) {
        col.renderCell = (p) => {
          const text = p.value == null || p.value === '' ? '—' : String(p.value);
          return (
            <Tooltip title={text.length > 18 ? text : ''}>
              <Typography variant="body2" noWrap fontWeight={role === 'reference' ? 700 : undefined}>{text}</Typography>
            </Tooltip>
          );
        };
      }
      return col;
    });
    mapped.push({
      field: 'actions',
      type: 'actions',
      headerName: 'View',
      width: 80,
      getActions: (p) => {
        const path = opsEntityPath(p.row as Record<string, unknown>, { slug, dataSource: meta?.dataSource });
        if (!path) return [];
        return [
          <GridActionsCellItem
            key="view"
            icon={<Visibility fontSize="small" />}
            label="View details"
            onClick={() => openRow(p.row as Record<string, unknown>)}
          />,
        ];
      },
    });
    return mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, selected, slug, isPartnerWise, meta?.dataSource]);

  const patch = (key: string, value: string) => setDraft((prev) => ({ ...prev, [key]: value }));

  const run = () => {
    if (hasFilterErrors) return;
    setApplied({ filters: { ...draft }, partners: [...draftPartners] });
    setRunCount((n) => n + 1);
  };

  const clearFilters = () => {
    setDraft({});
    setDraftPartners([]);
    setFromPickerError(null);
    setToPickerError(null);
    setApplied({ filters: {}, partners: [] });
    setRunCount((n) => n + 1);
  };

  const removeApplied = (key: string) => {
    if (key === '__partners') {
      setDraftPartners([]);
      setApplied((prev) => ({ ...prev, partners: [] }));
      return;
    }
    setDraft((prev) => ({ ...prev, [key]: '' }));
    setApplied((prev) => ({ ...prev, filters: { ...prev.filters, [key]: '' } }));
  };

  const genericDefs = (selected?.filters || []).filter((f) => {
    const key = String(f.key);
    return !['dateFrom', 'dateTo', 'status'].includes(key) && f.type !== 'date';
  });

  const filterLabel = (key: string): string => {
    if (key === 'dateFrom') return 'From';
    if (key === 'dateTo') return 'To';
    if (key === 'status') return isPartnerWise ? 'Trip status' : 'Status';
    if (key === 'search') return 'Search';
    if (key === 'customerUsername') return identity?.customer?.label || 'Customer';
    if (key === 'driverUsername') return identity?.partner?.label || 'Driver';
    if (key === 'referenceId') return identity?.reference?.label || 'Reference ID';
    if (key === 'zoneId') return 'Zone';
    if (key === 'paymentMethod') return 'Payment';
    if (key === 'tripType') return 'Trip type';
    const def = genericDefs.find((f) => String(f.key) === key);
    return String(def?.label || titleCase(key));
  };

  const filterValueLabel = (key: string, value: string): string => {
    if (key === 'dateFrom' || key === 'dateTo') return dayjs(value).format(DISPLAY_DATE_FORMAT);
    if (key === 'zoneId') return zones.find((z) => z._id === value)?.name || value;
    if (key === 'status' || key === 'paymentMethod' || key in filterOptions) return titleCase(value);
    return `“${value}”`;
  };

  const appliedChips = [
    ...Object.entries(applied.filters)
      .filter(([, v]) => v && v.trim())
      .map(([k, v]) => ({ key: k, label: `${filterLabel(k)}: ${filterValueLabel(k, v.trim())}` })),
    ...(applied.partners.length
      ? [{ key: '__partners', label: `${identity?.partner?.label || 'Driver'}: ${applied.partners.map((p) => [p.firstName, p.lastName].filter(Boolean).join(' ') || p.phone).join(', ')}` }]
      : []),
  ];

  const draftCount = Object.values(draft).filter((v) => v && v.trim()).length + (draftPartners.length ? 1 : 0);
  const pendingChanges = JSON.stringify({ f: Object.fromEntries(Object.entries(draft).filter(([, v]) => v)), p: draftPartners.map((p) => p._id) })
    !== JSON.stringify({ f: Object.fromEntries(Object.entries(applied.filters).filter(([, v]) => v)), p: applied.partners.map((p) => p._id) });

  const title = selected?.name || (isTripRequest ? 'Trip Request Report' : isPartnerWise ? 'Partner Wise Report' : slug);
  const searchPlaceholder = isTripRequest
    ? 'Trip ID, pickup, drop, customer or driver name / phone'
    : isPartnerWise
      ? 'Driver name, email or phone'
      : `${identity?.reference?.label || 'Reference'}, text fields, customer or partner name / phone`;

  const total = meta?.total ?? 0;
  const returned = meta?.returned ?? result?.rows?.length ?? 0;
  const truncated = returned < total;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Reports"
        title={title}
        subtitle={selected ? `${selected.slug} · ${titleCase(selected.module)}` : 'Filtered report with search and export'}
        breadcrumbs={[
          { label: 'Reports', href: '/reports' },
          { label: title },
        ]}
        actions={
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate('/reports')}>
            All reports
          </Button>
        }
      />

      <Paper elevation={0} sx={{ mb: 2, borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'flex-start' }}
          sx={{ px: 2, pt: 1.5, pb: 0.5 }}
        >
          <TextField
            size="small"
            label="Search report data"
            placeholder={searchPlaceholder}
            value={draft.search || ''}
            onChange={(e) => patch('search', e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            helperText="Searches all matching records on the server (partial match). Press Enter or Run to apply."
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 260, maxWidth: 560 }}
          />
          <Button
            size="small"
            variant={filtersOpen || draftCount > 0 ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setFiltersOpen((v) => !v)}
            sx={{ mt: { sm: 0.5 } }}
          >
            Filters{draftCount > 0 ? ` (${draftCount})` : ''}
          </Button>
          <Tooltip title={hasFilterErrors ? 'Fix the highlighted date fields first' : pendingChanges ? 'Apply the filters you changed' : ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                color={pendingChanges ? 'warning' : 'primary'}
                startIcon={<PlayArrow />}
                onClick={run}
                disabled={isFetching || hasFilterErrors}
                sx={{ mt: { sm: 0.5 } }}
              >
                Run{pendingChanges ? ' (changes pending)' : ''}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Collapse in={filtersOpen} unmountOnExit>
          <Box sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={800}>Advanced filters</Typography>
              {(draftCount > 0 || appliedChips.length > 0) && (
                <Button size="small" startIcon={<ClearAll />} onClick={clearFilters}>Clear all</Button>
              )}
            </Stack>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="From date"
                    format={DISPLAY_DATE_FORMAT}
                    value={draft.dateFrom ? dayjs(draft.dateFrom) : null}
                    maxDate={draft.dateTo ? dayjs(draft.dateTo) : undefined}
                    disableFuture
                    onError={setFromPickerError}
                    onChange={(v) => patch('dateFrom', v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true, sx: fieldSx, error: !!fromError, helperText: fromError || ' ' },
                      field: { clearable: true },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="To date"
                    format={DISPLAY_DATE_FORMAT}
                    value={draft.dateTo ? dayjs(draft.dateTo) : null}
                    minDate={draft.dateFrom ? dayjs(draft.dateFrom) : undefined}
                    disableFuture
                    onError={setToPickerError}
                    onChange={(v) => patch('dateTo', v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true, sx: fieldSx, error: !!toError, helperText: toError || ' ' },
                      field: { clearable: true },
                    }}
                  />
                </Grid>
                {statusOptions.length > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField select fullWidth size="small" label={isPartnerWise ? 'Trip status' : 'Status'}
                      value={draft.status || ''} onChange={(e) => patch('status', e.target.value)}
                      InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}
                      helperText=" " sx={fieldSx}>
                      <MenuItem value="">All statuses</MenuItem>
                      {statusOptions.map((s) => <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}

                {isOperational && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select fullWidth size="small" label="Zone"
                        value={draft.zoneId || ''} onChange={(e) => patch('zoneId', e.target.value)}
                        InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}
                        helperText=" " sx={fieldSx}>
                        <MenuItem value="">All zones</MenuItem>
                        {zones.map((z) => <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <PartnerMultiSelect
                        value={draftPartners}
                        onChange={setDraftPartners}
                        entityType="driver"
                        label="Drivers"
                        placeholder="Select one or more drivers"
                      />
                    </Grid>
                    {isTripRequest && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField select fullWidth size="small" label="Payment"
                            value={draft.paymentMethod || ''} onChange={(e) => patch('paymentMethod', e.target.value)}
                            InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={fieldSx}>
                            <MenuItem value="">All payments</MenuItem>
                            {PAYMENT_OPTIONS.map((s) => <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField select fullWidth size="small" label="Trip type"
                            value={draft.tripType || ''} onChange={(e) => patch('tripType', e.target.value)}
                            InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={fieldSx}>
                            <MenuItem value="">All trip types</MenuItem>
                            {TRIP_TYPES.map((s) => <MenuItem key={s} value={s}>{titleCase(s.toLowerCase())}</MenuItem>)}
                          </TextField>
                        </Grid>
                      </>
                    )}
                  </>
                )}

                {!isOperational && genericDefs.map((f) => {
                  const key = String(f.key);
                  const options = filterOptions[key];
                  return (
                    <Grid item xs={12} sm={6} md={3} key={key}>
                      {options?.length ? (
                        <TextField select fullWidth size="small" label={String(f.label || titleCase(key))}
                          value={draft[key] || ''} onChange={(e) => patch(key, e.target.value)}
                          InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}
                          helperText=" " sx={fieldSx}>
                          <MenuItem value="">All</MenuItem>
                          {options.map((o) => <MenuItem key={o} value={o}>{titleCase(o)}</MenuItem>)}
                        </TextField>
                      ) : (
                        <TextField fullWidth size="small" label={String(f.label || titleCase(key))}
                          value={draft[key] || ''} onChange={(e) => patch(key, e.target.value)}
                          helperText=" " sx={fieldSx} />
                      )}
                    </Grid>
                  );
                })}

                {identity?.customer && (
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" label={identity.customer.label}
                      placeholder="Name, email or phone"
                      helperText="Partial match: “ram” also finds Ramesh"
                      value={draft.customerUsername || ''} onChange={(e) => patch('customerUsername', e.target.value)}
                      sx={fieldSx} />
                  </Grid>
                )}
                {identity?.partner && !isOperational && (
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" label={identity.partner.label}
                      placeholder="Name, email or phone"
                      helperText={`Partial match; see the ${identity.partner.label} column`}
                      value={draft.driverUsername || ''} onChange={(e) => patch('driverUsername', e.target.value)}
                      sx={fieldSx} />
                  </Grid>
                )}
                {identity?.reference && (
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" label={identity.reference.label}
                      placeholder={`Full or partial ${identity.reference.label}`}
                      helperText={`Matches the ${identity.reference.label} column`}
                      value={draft.referenceId || ''} onChange={(e) => patch('referenceId', e.target.value)}
                      sx={fieldSx} />
                  </Grid>
                )}
              </Grid>
            </LocalizationProvider>
          </Box>
        </Collapse>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden', p: 1.5 }}>
        <Stack spacing={1} sx={{ px: 1, pb: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            {truncated
              ? `Showing first ${returned.toLocaleString()} of ${total.toLocaleString()} matching records`
              : `${total.toLocaleString()} matching record${total === 1 ? '' : 's'}`}
            <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
              {' · Click a row or View to open the full trip, order or partner details'}
            </Typography>
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Applied filters:</Typography>
            {appliedChips.length === 0 ? (
              <Typography variant="caption" color="text.secondary">None (all records, all dates)</Typography>
            ) : appliedChips.map((c) => (
              <Chip key={c.key} size="small" color="primary" variant="outlined" label={c.label} onDelete={() => removeApplied(c.key)} />
            ))}
          </Stack>
          {truncated && (
            <Typography variant="caption" color="text.secondary">
              Only the first {returned.toLocaleString()} rows are loaded. Narrow the filters to see the rest.
            </Typography>
          )}
        </Stack>
        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {(error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not run this report.'}
          </Alert>
        )}
        <EnhancedDataGrid
          rows={result?.rows || []}
          columns={columns}
          loading={isFetching}
          getRowId={(r) => String((r as Record<string, unknown>)._id ?? (r as Record<string, unknown>).id ?? JSON.stringify(r))}
          autoHeight
          exportFileName={slug || 'report'}
          disableLocalSearch
          emptyTitle="No matching records"
          emptySubtitle={appliedChips.length ? 'Try removing a filter above.' : undefined}
          onRowClick={(p) => openRow(p.row as Record<string, unknown>)}
        />
      </Paper>
    </PageShell>
  );
}
